/**
 * Browser side of product analytics.
 *
 * Sent from the browser directly: the signup funnel (signup_*), because the
 * visitor has no account yet, and the Plans & Payments funnel (upgrade_*), because the
 * steps before payment -- viewing plans, opening checkout, a declined card --
 * never reach the server. upgrade_completed itself is sent by the server. Everything else after signup (signed_up,
 * job_created, report views...) is sent by the backend, see
 * backend/app/services/analytics_service.py. identifyUser() joins the two, so
 * a funnel can follow one person from the signup page onwards.
 *
 * This app renders candidate names, resumes and interview reports, none of
 * which should ever leave for a third party, so PostHog's automatic
 * collection is limited:
 *
 *   autocapture OFF          - no clicks or page markup are recorded.
 *   pageviews ON             - every route change and page leave. URLs carry
 *                              candidate, job and interview ids (ids only,
 *                              never names), accepted for now.
 *   session recording OFF    - the recording switch is ON at project level
 *                              (shared with the marketing site).
 *   identified_only          - no profile is created for anonymous visitors.
 *
 * No key configured means every function here is a no-op.
 */

const KEY = process.env.REACT_APP_POSTHOG_KEY;
const HOST = process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com";

// Which deployment sent the event. Local, sandbox and production share one
// PostHog project, so every event carries this to keep them apart.
// REACT_APP_ENVIRONMENT overrides; otherwise it is read from the address.
export const ENVIRONMENT = (() => {
  if (process.env.REACT_APP_ENVIRONMENT) return process.env.REACT_APP_ENVIRONMENT;
  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  if (hostname === "localhost" || hostname === "127.0.0.1") return "development";
  if (hostname.includes("sandbox")) return "sandbox";
  return "production";
})();

// Attached to every browser event. Registered again after reset(), which
// clears registered properties.
const registerSuperProperties = () => ph().register({ event_source: "client", environment: ENVIRONMENT });

// Our own team and QA, flagged is_internal after login. Same list as the
// backend's ANALYTICS_INTERNAL_DOMAINS.
const INTERNAL_EMAIL_DOMAINS = (process.env.REACT_APP_ANALYTICS_INTERNAL_DOMAINS || "nsquarexperts.com,geniehire.ai")
  .split(",")
  .map((d) => d.trim().toLowerCase())
  .filter(Boolean);

export const isInternalEmail = (email) => {
  const domain = String(email || "").split("@")[1]?.toLowerCase();
  return Boolean(domain) && INTERNAL_EMAIL_DOMAINS.includes(domain);
};

/** The complete list of what this app sends. Anything else passed to track() is dropped. */
export const EVENTS = {
  // Someone opened the signup form (not the login form)
  SIGNUP_PAGE_VIEWED: "signup_page_viewed",

  // ---- Signup funnel, in order ----
  // Step 1 (work email) submitted
  SIGNUP_SUBMITTED: "signup_submitted",
  // Step 1 accepted and the verification code emailed
  SIGNUP_CODE_SENT: "signup_code_sent",
  // Asked for another code
  SIGNUP_CODE_RESENT: "signup_code_resent",
  // Step 2: entered the code
  SIGNUP_CODE_SUBMITTED: "signup_code_submitted",
  // Code accepted. The account itself is created at step 3.
  SIGNUP_VERIFIED: "signup_verified",
  // Step 3 (name, phone, password) submitted
  SIGNUP_DETAILS_SUBMITTED: "signup_details_submitted",
  // Workspace created
  SIGNUP_COMPLETED: "signup_completed",
  // Any step refused (step, error_code, status)
  SIGNUP_FAILED: "signup_failed",
  // Field level: which box people stall on, and for how long. Field names
  // only, never what was typed.
  SIGNUP_FIELD_FOCUSED: "signup_field_focused",
  SIGNUP_FIELD_COMPLETED: "signup_field_completed",
  SIGNUP_FIELD_SKIPPED: "signup_field_skipped",
  SIGNUP_FORM_ABANDONED: "signup_form_abandoned",
  // Phones and tablets: step 1 emails a link to open on a laptop
  SIGNUP_LINK_SENT: "signup_link_sent",
  // "Resend the link" / "Email me a new link" (after_expiry)
  SIGNUP_LINK_RESENT: "signup_link_resent",
  // The emailed link opened (device: phone | laptop; status: valid | expired | used | invalid)
  SIGNUP_LINK_OPENED: "signup_link_opened",
  // The company already has an account: "Ask to join" or "Send a reminder" (reminder)
  SIGNUP_JOIN_REQUESTED: "signup_join_requested",

  // A phone or tablet reached a desktop-only page and saw the welcome screen
  MOBILE_WELCOME_SHOWN: "mobile_welcome_shown",

  // ---- Plans & Payments (self-serve purchase funnel) ----
  // Properties are plan and payment facts only (plan_id, kind, packs, amount,
  // currency, billing_cycle, failure reason) -- never names or emails.
  // Opened the Plans & Payments page
  UPGRADE_PAGE_VIEWED: "upgrade_page_viewed",
  // Confirmed a plan or top-up purchase (Pay on the confirmation)
  UPGRADE_CLICKED: "upgrade_clicked",
  // Razorpay's payment window opened
  UPGRADE_CHECKOUT_OPENED: "upgrade_checkout_opened",
  // (upgrade_completed is sent by the server when the plan or top-up is
  // applied, so a buyer who closes the tab after paying is still counted.)
  // Payment declined in Razorpay, or paid but not applied (reason says which)
  UPGRADE_PAYMENT_FAILED: "upgrade_payment_failed",
};

const ALLOWED = new Set(Object.values(EVENTS));

let ready = false;
let failed = false;
// Events fired before the script finishes downloading are held here rather than
// dropped -- the page view usually happens within the first second.
let pending = [];

const ph = () => (typeof window !== "undefined" ? window.posthog : undefined);

export function initAnalytics() {
  if (ready || failed || !KEY || typeof window === "undefined") return;
  if (ph() && ph().__loaded) {
    ready = true;
    return;
  }

  const script = document.createElement("script");
  script.src = `${HOST.replace(".i.posthog.com", "-assets.i.posthog.com")}/static/array.js`;
  script.async = true;
  script.crossOrigin = "anonymous";

  script.onerror = () => {
    // Blocked by an ad blocker, or offline. Give up quietly: analytics must
    // never interfere with someone signing up.
    failed = true;
    pending = [];
  };

  script.onload = () => {
    try {
      ph().init(KEY, {
        api_host: HOST,
        autocapture: false,
        // A $pageview on first load and on every in-app route change (this is
        // a single-page app), and a $pageleave when a page is left
        capture_pageview: "history_change",
        capture_pageleave: true,
        disable_session_recording: true,
        enable_recording_console_log: false,
        person_profiles: "identified_only",
        loaded: () => {
          registerSuperProperties();
          ready = true;
          pending.forEach((item) =>
            item.identify ? item.identify() : ph().capture(item.event, item.properties)
          );
          pending = [];
        },
      });
    } catch (error) {
      failed = true;
      pending = [];
    }
  };

  document.head.appendChild(script);
}

/** Record one of the EVENTS above. Safe to call anywhere, at any time.
 *  options go to posthog.capture, e.g. {transport: "sendBeacon"} for an event
 *  sent while the tab is closing. */
export function track(event, properties = {}, options) {
  if (!KEY || failed || !ALLOWED.has(event)) return;
  if (ready && ph()) {
    ph().capture(event, properties, options);
  } else if (pending.length < 10) {
    pending.push({ event, properties });
  }
}

/** Tie this browser to the signed-in user: at the end of signup, after every
 *  login and whenever the app opens with a session. The first call links the
 *  anonymous signup-page visit to the account. userId is the Cognito sub, the
 *  same id the backend sends events under; email, name and phone are the
 *  recruiter's own (never a candidate's). wa_opt_in is the signup checkbox
 *  (Privacy Policy, Terms and WhatsApp updates), sent only when known. */
export function identifyUser(userId, { email, name, phone, waOptIn } = {}) {
  if (!KEY || failed || !userId) return;
  const traits = {};
  if (email) traits.email = email;
  if (name) traits.name = name;
  if (phone) traits.phone = phone;
  if (typeof waOptIn === "boolean") traits.wa_opt_in = waOptIn;
  const run = () => {
    ph()?.identify(userId, traits);
    // On every later event in this session; reset() on logout clears it
    if (email) ph()?.register({ is_internal: isInternalEmail(email) });
  };
  if (ready && ph()) run();
  else if (pending.length < 10) pending.push({ identify: run });
}

/** Attach the signed-in session to its company, so browser events carry the
 *  same company group the server sends. The name and plan are sent by the
 *  server (analytics_service._identify_company), so only the id is needed. */
export function setCompany(companyId) {
  if (!KEY || failed || !companyId) return;
  const run = () => ph()?.group("company", String(companyId));
  if (ready && ph()) run();
  else if (pending.length < 10) pending.push({ identify: run });
}

/** On logout: the next person on this browser starts as a new anonymous
 *  visitor instead of being merged into the user who just left. */
export function resetAnalytics() {
  if (!KEY || failed) return;
  pending = pending.filter((item) => !item.identify);
  if (!ready || !ph()) return;
  try {
    ph().reset();
    registerSuperProperties();
  } catch {
    /* analytics must never get in the way of logging out */
  }
}

/**
 * Watches one signup form and reports which field people stall on: the field's
 * NAME and how long it was open, never what was typed into it.
 *
 * Usage:
 *   const fields = useRef(createFieldTracker("signup_details")).current;
 *   <input {...fields.bind("phoneNumber")} ... />
 *   // leaving without submitting: fields.abandoned()
 */
export function createFieldTracker(form) {
  const firstSeen = new Set();
  const openedAt = {};
  const filled = new Set();
  let lastField = null;
  let abandonedSent = false;

  return {
    onFocus(field) {
      openedAt[field] = Date.now();
      lastField = field;
      // Once per field: re-focusing after a correction is not a new visitor.
      if (!firstSeen.has(field)) {
        firstSeen.add(field);
        track(EVENTS.SIGNUP_FIELD_FOCUSED, { form, field });
      }
    },

    onBlur(field, hasValue) {
      const seconds = openedAt[field] ? Math.round((Date.now() - openedAt[field]) / 1000) : null;
      if (hasValue) {
        filled.add(field);
        track(EVENTS.SIGNUP_FIELD_COMPLETED, { form, field, seconds });
      } else {
        filled.delete(field);
        track(EVENTS.SIGNUP_FIELD_SKIPPED, { form, field, seconds });
      }
    },

    /** Spread onto an input: {...fields.bind("phoneNumber")} */
    bind(field) {
      return {
        onFocus: () => this.onFocus(field),
        onBlur: (e) => this.onBlur(field, Boolean((e?.target?.value || "").trim())),
      };
    },

    /** Left without submitting. Sent once, and only if a field was touched. */
    abandoned() {
      if (abandonedSent || !firstSeen.size) return;
      abandonedSent = true;
      track(
        EVENTS.SIGNUP_FORM_ABANDONED,
        {
          form,
          last_field: lastField,
          fields_touched: firstSeen.size,
          fields_filled: filled.size,
          filled_fields: Array.from(filled).sort(),
          empty_fields: Array.from(firstSeen).filter((f) => !filled.has(f)).sort(),
        },
        // Usually sent as the tab closes, when a normal request is cancelled
        { transport: "sendBeacon" }
      );
    },
  };
}

// ---- First-touch campaign parameters, passed to the server with signed_up ----

const ATTRIBUTION_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "fbclid", "gclid"];
const ATTRIBUTION_STORE = "gh_first_touch";

/** Remember the campaign parameters of the first visit that carried any. */
export function captureAttribution() {
  try {
    if (localStorage.getItem(ATTRIBUTION_STORE)) return;
    const params = new URLSearchParams(window.location.search);
    const found = {};
    ATTRIBUTION_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v) found[k] = v.slice(0, 200);
    });
    if (Object.keys(found).length) localStorage.setItem(ATTRIBUTION_STORE, JSON.stringify(found));
  } catch {
    /* storage blocked: attribution is optional */
  }
}

export function getAttribution() {
  try {
    return JSON.parse(localStorage.getItem(ATTRIBUTION_STORE) || "null");
  } catch {
    return null;
  }
}
